import { Component, OnInit, ViewChild, AfterViewInit, TemplateRef } from '@angular/core';
import { SystemUserService } from '../../../../../DTO/SystemUser/SystemUser.service';
import { SystemUserDTO } from '../../../../../DTO/SystemUser/SystemUserDTO';
import { UserRoleAssignmentDTO } from '../../../../../DTO/SystemUser/UserRoleAssignmentDTO';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../../../material.module';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { LocalizationPipe } from '../../../../../pipe/localization-pipe';
import { LocalizationService } from '../../../../../services/localizationService';
import { PermissionService } from '../../../../../../core/authorization/permission.service';
import { DataFilter } from '../../../../../../core/DataFilter';
import { MatDialog } from '@angular/material/dialog';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ToastrService } from 'ngx-toastr';
import { SystemRole } from '../../../role-management/system-role-management/system-role-management.component';
import { RoleService } from '../../../../../services/apps/role/role.service';
import { AppDeleteDialogComponent } from '../../../contact-list/delete-dialog/delete-dialog.component';

@Component({
  selector: 'app-system-user-list',
  templateUrl: './system-user-list.component.html',
  standalone: true,
  imports: [CommonModule, MaterialModule, TablerIconsModule, FormsModule, ReactiveFormsModule, LocalizationPipe]
})
export class SystemUserListComponent implements OnInit, AfterViewInit {
  @ViewChild('keycloakUserModal') keycloakUserModal: TemplateRef<any>;
  @ViewChild('roleAssignmentModal') roleAssignmentModal: TemplateRef<any>;

  displayedColumns: string[] = ['id', 'userId', 'firstName', 'lastName', 'email','isEnabled', 'actions'];
  dataSource = new MatTableDataSource<SystemUserDTO>([]);
  isLoading = false;
  totalItems = 0;
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];
  filter = new DataFilter();
  searchName: string = '';
  pageIndex = 0;
  statusFilter: boolean | null = null;

  // Keycloak kullanıcıları için
  keycloakUsers: SystemUserDTO[] = [];
  originalKeycloakUsers: SystemUserDTO[] = [];  // Orijinal veriyi saklamak için
  selectedKeycloakUsers: SystemUserDTO[] = [];
  keycloakSearchText: string = '';
  isKeycloakLoading = false;

  // Role assignment için
  currentUserForRoleAssignment: SystemUserDTO | null = null;
  availableRoles: SystemRole[] = [];
  originalAvailableRoles: SystemRole[] = [];
  selectedRoles: SystemRole[] = [];
  userOriginalRoles: SystemRole[] = []; // Kullanıcının orijinal rolleri
  roleSearchText: string = '';
  isRoleLoading = false;

  // Sütun başlıkları için eşleme
  columnHeaderMap = {
    'id': 'SystemUser.ID',
    'userId': 'SystemUser.Username',
    'firstName': 'SystemUser.FirstName',
    'lastName': 'SystemUser.SurName',
    'email': 'SystemUser.Email',
    'isEnabled': 'SystemUser.Active',
    'actions': 'SystemUser.Transactions',
  };

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private systemUserService: SystemUserService,
    private router: Router,
    public localizationService: LocalizationService,
    public permissionService: PermissionService,
    public dialog: MatDialog,
    private toastr: ToastrService,
    private roleService: RoleService
  ) { }

  ngOnInit(): void {
    this.loadSystemUsers(0, this.pageSize);
  }

  ngAfterViewInit(): void {
    if (this.sort) {
      this.sort.sortChange.subscribe(() => {
        this.resetToFirstPage();
      });
    }
  }

  resetToFirstPage(): void {
    this.pageIndex = 0;
    this.loadSystemUsers(0, this.pageSize);
  }

  onPageChange(event: any): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadSystemUsers(this.pageIndex, this.pageSize);
  }

  loadSystemUsers(page: number, pageSize: number): void {
    this.isLoading = true;
    const apiPage = page + 1;
    const params = {
      page: apiPage,
      size: pageSize,
      sortField: this.sort?.active?.toUpperCase() || 'ID',
      sortType: this.sort?.direction === 'desc' ? 'DESC' : 'ASC'
    };

    this.systemUserService.filterSystemUsers(params, this.filter.getFilterAsJson()).subscribe({
      next: (response: any) => {
        this.dataSource.data = response.data.content || [];
        this.totalItems = response.data.totalElements || 0;
        this.pageIndex = page;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading system users', error);
        this.isLoading = false;
      }
    });
  }

  applyStatusFilter(): void {
    if (this.statusFilter !== null) {
      this.filter.add('isEnabled', this.statusFilter.toString());
    } else {
      this.filter.remove('isEnabled');
    }
    this.resetToFirstPage();
  }

  applyNameFilter(): void {
    if (this.searchName && this.searchName.trim() !== '') {
      this.filter.add('search', this.searchName.trim());
    } else {
      this.filter.remove('search');
    }
    this.resetToFirstPage();
  }

  getColumnHeader(column: string): string {
    return this.columnHeaderMap[column] || column;
  }

  deleteSystemUser(user: SystemUserDTO): void {
    const dialogRef = this.dialog.open(AppDeleteDialogComponent, {
      width: '400px',
      autoFocus: false,
      data: {
        message: `${user.firstName} ${user.lastName} kullanıcısını silmek istediğinizden emin misiniz?`,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.isLoading = true;
        this.systemUserService.deleteSystemUser(user.id).subscribe({
          next: () => {
            this.loadSystemUsers(this.pageIndex, this.pageSize);
            this.toastr.success(
              this.localizationService.getLocalizationValue('SystemUser.DeleteSuccess'),
              this.localizationService.getLocalizationValue('Common.Ok')
            );
          },
                error: (error) => {
            console.error('Error deleting user:', error);
            this.isLoading = false;
            this.toastr.error(
              this.localizationService.getLocalizationValue('SystemUser.DeleteError'),
              this.localizationService.getLocalizationValue('Common.Error')
            );
          }
        });
      }
    });
  }

  toggleUserStatus(user: SystemUserDTO): void {
    this.isLoading = true;
    this.systemUserService.toggleUserStatus(user.id).subscribe({
      next: (updatedUser) => {
        this.loadSystemUsers(this.pageIndex, this.pageSize);
        this.toastr.success(
          this.localizationService.getLocalizationValue('SystemUser.StatusUpdateSuccess'),
          this.localizationService.getLocalizationValue('Common.Ok')
        );
      },
      error: (error) => {
        console.error('Error toggling user status:', error);
        const index = this.dataSource.data.findIndex(u => u.id === user.id);
        if (index !== -1) {
          this.dataSource.data[index].isEnabled = !this.dataSource.data[index].isEnabled;
          this.dataSource._updateChangeSubscription();
        }
        this.isLoading = false;
        this.toastr.error(
          this.localizationService.getLocalizationValue('SystemUser.StatusUpdateError'),
          this.localizationService.getLocalizationValue('Common.Error')
        );
      }
    });
  }


  closeKeycloakModal(): void {
    this.selectedKeycloakUsers = [];
    this.keycloakSearchText = '';
    this.keycloakUsers = [...this.originalKeycloakUsers]; // Orijinal veriyi geri yükle
    this.dialog.closeAll();
  }

  openKeycloakUserModal(): void {
    const dialogRef = this.dialog.open(this.keycloakUserModal, {
      width: '1400px',
      height: '85vh',
      maxWidth: '98vw',
      maxHeight: '98vh',
      panelClass: 'responsive-dialog'
    });

    // Modal açıldığında istek at
    this.isKeycloakLoading = true;
    const params = {
      page: 1,
      size: 100,
      sortField: 'ID',
      sortType: 'ASC'
    };

    this.systemUserService.getUnregisteredKeycloakUsers(params, {}).subscribe({
      next: (response: any) => {
        if (response && response.data && response.data.content) {
          this.keycloakUsers = response.data.content;
          this.originalKeycloakUsers = [...response.data.content]; // Orijinal veriyi sakla
        } else {
          this.keycloakUsers = [];
          this.originalKeycloakUsers = [];
        }
        this.isKeycloakLoading = false;
      },
      error: (error) => {
        console.error('Error loading keycloak users', error);
        this.keycloakUsers = [];
        this.originalKeycloakUsers = [];
        this.isKeycloakLoading = false;
      }
    });

    // Modal kapandığında seçimleri temizle
    dialogRef.afterClosed().subscribe(() => {
      this.closeKeycloakModal();
    });
  }

  onKeycloakUserSelection(event: MatCheckboxChange, user: SystemUserDTO): void {
    if (event.checked) {
      if (!this.selectedKeycloakUsers.some(u => u.userId === user.userId)) {
        this.selectedKeycloakUsers.push(user);
      }
    } else {
      const index = this.selectedKeycloakUsers.findIndex(u => u.userId === user.userId);
      if (index > -1) {
        this.selectedKeycloakUsers.splice(index, 1);
      }
    }
  }

  removeKeycloakUserFromSelection(user: SystemUserDTO): void {
    const index = this.selectedKeycloakUsers.findIndex(u => u.userId === user.userId);
    if (index > -1) {
      this.selectedKeycloakUsers.splice(index, 1);
    }
  }

  isUserSelected(user: SystemUserDTO): boolean {
    return this.selectedKeycloakUsers.some(u => u.userId === user.userId);
  }

  isAllKeycloakUsersSelected(): boolean {
    return this.keycloakUsers.length > 0 &&
           this.keycloakUsers.every(user => this.selectedKeycloakUsers.some(u => u.userId === user.userId));
  }

  isSomeKeycloakUsersSelected(): boolean {
    return this.selectedKeycloakUsers.length > 0 &&
           !this.isAllKeycloakUsersSelected();
  }

  toggleAllKeycloakUsers(event: MatCheckboxChange): void {
    if (event.checked) {
      // Sadece henüz seçili olmayan kullanıcıları ekle
      this.keycloakUsers.forEach(user => {
        if (!this.selectedKeycloakUsers.some(u => u.userId === user.userId)) {
          this.selectedKeycloakUsers.push(user);
        }
      });
    } else {
      this.selectedKeycloakUsers = [];
    }
  }

  applyKeycloakUserSelection(): void {
    if (this.selectedKeycloakUsers.length === 0) {
      this.toastr.info(
        this.localizationService.getLocalizationValue('SystemUser.NoUserSelected') || 'Hiç kullanıcı seçilmedi',
        this.localizationService.getLocalizationValue('Common.Info')
      );
      this.dialog.closeAll();
      return;
    }

    this.isKeycloakLoading = true;
    this.systemUserService.createFromKeycloak(this.selectedKeycloakUsers).subscribe({
      next: (response) => {
        this.isKeycloakLoading = false;
        this.toastr.success(
          this.localizationService.getLocalizationValue('SystemUser.CreateFromKeycloakSuccess') || 'Kullanıcılar başarıyla oluşturuldu',
          this.localizationService.getLocalizationValue('Common.Success')
        );
        this.dialog.closeAll();
        this.loadSystemUsers(this.pageIndex, this.pageSize); // Listeyi yenile
      },
      error: (error) => {
        console.error('Error creating users:', error);
        this.isKeycloakLoading = false;
        this.toastr.error(
          this.localizationService.getLocalizationValue('SystemUser.CreateFromKeycloakError') || 'Kullanıcı oluşturma başarısız',
          this.localizationService.getLocalizationValue('Common.Error')
        );
      }
    });
  }

  filterKeycloakUsers(): void {
    if (this.keycloakSearchText && this.keycloakSearchText.trim() !== '') {
      // Her zaman orijinal veri üzerinde filtreleme yap
      this.keycloakUsers = this.originalKeycloakUsers.filter(user =>
        user.firstName.toLowerCase().includes(this.keycloakSearchText.toLowerCase()) ||
        user.lastName.toLowerCase().includes(this.keycloakSearchText.toLowerCase()) ||
        user.email.toLowerCase().includes(this.keycloakSearchText.toLowerCase()) ||
        user.userId.toLowerCase().includes(this.keycloakSearchText.toLowerCase())
      );
    } else {
      // Arama metni boşsa orijinal veriyi geri yükle
      this.keycloakUsers = [...this.originalKeycloakUsers];
    }
  }


  openRoleAssignmentModal(user: SystemUserDTO): void {
    this.currentUserForRoleAssignment = user;

    const dialogRef = this.dialog.open(this.roleAssignmentModal, {
      width: '1400px',
      height: '85vh',
      maxWidth: '98vw',
      maxHeight: '98vh',
      panelClass: 'responsive-dialog'
    });

    // Önce available rolleri yükle, sonra mevcut rolleri seç
    this.loadAvailableRolesAndUserRoles(user.userId);

    // Modal kapandığında seçimleri temizle
    dialogRef.afterClosed().subscribe(() => {
      this.closeRoleAssignmentModal();
    });
  }

  closeRoleAssignmentModal(): void {
    this.currentUserForRoleAssignment = null;
    this.selectedRoles = [];
    this.userOriginalRoles = [];
    this.roleSearchText = '';
    this.availableRoles = [...this.originalAvailableRoles];
    this.dialog.closeAll();
  }

  loadAvailableRoles(): void {
    this.isRoleLoading = true;

    this.roleService.getSystemRoles().subscribe({
      next: (response: any) => {
        if (response && response.data && response.data.content) {
          this.availableRoles = response.data.content;
          this.originalAvailableRoles = [...response.data.content];
        } else {
          this.availableRoles = [];
          this.originalAvailableRoles = [];
        }
        this.isRoleLoading = false;
      },
      error: (error) => {
        console.error('Error loading system roles', error);
        this.availableRoles = [];
        this.originalAvailableRoles = [];
        this.isRoleLoading = false;
        this.toastr.error(
          this.localizationService.getLocalizationValue('SystemUser.RoleLoadError') || 'Roller yüklenirken hata oluştu',
          this.localizationService.getLocalizationValue('Common.Error')
        );
      }
    });
  }

  loadUserCurrentRoles(userId: string): void {
    this.isRoleLoading = true;
    this.systemUserService.getKeycloakUserRoles(userId).subscribe({
      next: (response: any) => {
        if (response && response.data) {
          const userRoleNames = response.data;

          // Kullanıcının mevcut rollerini selectedRoles'a ekle
          setTimeout(() => {
            this.selectedRoles = this.availableRoles.filter(role =>
              userRoleNames.includes(role.name)
            );
            this.isRoleLoading = false;
          }, 100);
        } else {
          this.isRoleLoading = false;
        }
      },
      error: (error) => {
        console.error('Error loading user current roles', error);
        this.isRoleLoading = false;
        this.toastr.error(
          this.localizationService.getLocalizationValue('SystemUser.UserRoleLoadError') || 'Kullanıcı rolleri yüklenirken hata oluştu',
          this.localizationService.getLocalizationValue('Common.Error')
        );
      }
    });
  }

  loadAvailableRolesAndUserRoles(userId: string): void {
    this.isRoleLoading = true;

    // Önce available rolleri yükle
    this.roleService.getSystemRoles().subscribe({
      next: (response: any) => {
        if (response && response.data && response.data.content) {
          this.availableRoles = response.data.content;
          this.originalAvailableRoles = [...response.data.content];

          // Available roller yüklendikten sonra kullanıcının mevcut rollerini yükle
          this.systemUserService.getKeycloakUserRoles(userId).subscribe({
            next: (userRolesResponse: any) => {
              if (userRolesResponse && userRolesResponse.data) {
                const userRoleNames = userRolesResponse.data; // List<String> role names

                // Kullanıcının mevcut rollerini selectedRoles'a ekle
                this.selectedRoles = this.availableRoles.filter(role =>
                  userRoleNames.includes(role.name)
                );
                // Orijinal rolleri de sakla
                this.userOriginalRoles = [...this.selectedRoles];
              }
              this.isRoleLoading = false;
            },
            error: (error) => {
              console.error('Error loading user current roles', error);
              this.isRoleLoading = false;
              this.toastr.error(
                this.localizationService.getLocalizationValue('SystemUser.UserRoleLoadError') || 'Kullanıcı rolleri yüklenirken hata oluştu',
                this.localizationService.getLocalizationValue('Common.Error')
              );
            }
          });
        } else {
          this.availableRoles = [];
          this.originalAvailableRoles = [];
          this.isRoleLoading = false;
        }
      },
      error: (error) => {
        console.error('Error loading system roles', error);
        this.availableRoles = [];
        this.originalAvailableRoles = [];
        this.isRoleLoading = false;
        this.toastr.error(
          this.localizationService.getLocalizationValue('SystemUser.RoleLoadError') || 'Roller yüklenirken hata oluştu',
          this.localizationService.getLocalizationValue('Common.Error')
        );
      }
    });
  }

  onRoleSelection(event: MatCheckboxChange, role: SystemRole): void {
    if (event.checked) {
      if (!this.selectedRoles.some(r => r.id === role.id)) {
        this.selectedRoles.push(role);
      }
    } else {
      const index = this.selectedRoles.findIndex(r => r.id === role.id);
      if (index > -1) {
        this.selectedRoles.splice(index, 1);
      }
    }
  }

  isRoleSelected(role: SystemRole): boolean {
    return this.selectedRoles.some(r => r.id === role.id);
  }

  isAllRolesSelected(): boolean {
    return this.availableRoles.length > 0 &&
           this.availableRoles.every(role => this.selectedRoles.some(r => r.id === role.id));
  }

  isSomeRolesSelected(): boolean {
    return this.selectedRoles.length > 0 && !this.isAllRolesSelected();
  }

  toggleAllRoles(event: MatCheckboxChange): void {
    if (event.checked) {
      this.availableRoles.forEach(role => {
        if (!this.selectedRoles.some(r => r.id === role.id)) {
          this.selectedRoles.push(role);
        }
      });
    } else {
      this.selectedRoles = [];
    }
  }

  applyRoleAssignment(): void {
    if (!this.currentUserForRoleAssignment) {
      this.dialog.closeAll();
      return;
    }

    // Eğer hiç değişiklik yoksa modalı kapat
    if (!this.hasRoleChanges()) {
      this.toastr.info(
        this.localizationService.getLocalizationValue('SystemUser.NoChangesDetected') || 'Herhangi bir değişiklik yapılmadı',
        this.localizationService.getLocalizationValue('Common.Info')
      );
      this.dialog.closeAll();
      return;
    }

    this.isRoleLoading = true;

    // assignRolesToUser kullanıcının TÜM rollerini belirtilen rollerle değiştirir
    const roleAssignmentDTO: UserRoleAssignmentDTO = {
      userId: this.currentUserForRoleAssignment.userId,
      roleNames: this.selectedRoles.map(role => role.name)
    };

    this.systemUserService.assignRolesToUser(roleAssignmentDTO).subscribe({
      next: (response) => {
        this.isRoleLoading = false;
        this.toastr.success(
          this.localizationService.getLocalizationValue('SystemUser.RoleUpdateSuccess') || 'Roller başarıyla güncellendi',
          this.localizationService.getLocalizationValue('Common.Success')
        );
        this.dialog.closeAll();
        this.loadSystemUsers(this.pageIndex, this.pageSize); // Listeyi yenile
      },
      error: (error) => {
        console.error('Error assigning roles:', error);
        this.isRoleLoading = false;
        this.toastr.error(
          this.localizationService.getLocalizationValue('SystemUser.RoleAssignmentError') || 'Rol atama başarısız',
          this.localizationService.getLocalizationValue('Common.Error')
        );
      }
    });
  }

  filterRoles(): void {
    if (this.roleSearchText && this.roleSearchText.trim() !== '') {
      this.availableRoles = this.originalAvailableRoles.filter(role =>
        role.name.toLowerCase().includes(this.roleSearchText.toLowerCase())
      );
    } else {
      this.availableRoles = [...this.originalAvailableRoles];
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    try {
      // "17-04-2025 11:27:48" formatını parse et
      const [datePart, timePart] = dateString.split(' ');
      const [day, month, year] = datePart.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString('tr-TR');
    } catch (error) {
      return dateString; // Parse edilemezse orijinal string'i dön
    }
  }

  removeRoleFromSelection(role: SystemRole): void {
    const index = this.selectedRoles.findIndex(r => r.id === role.id);
    if (index > -1) {
      this.selectedRoles.splice(index, 1);
    }
  }

  hasRoleChanges(): boolean {
    // Uzunluklar farklıysa değişiklik var
    if (this.selectedRoles.length !== this.userOriginalRoles.length) {
      return true;
    }

    // Her seçili rolün orijinal rollerde olup olmadığını kontrol et
    for (const selectedRole of this.selectedRoles) {
      if (!this.userOriginalRoles.some(originalRole => originalRole.id === selectedRole.id)) {
        return true;
      }
    }

    // Hiç değişiklik yok
    return false;
  }
}
